import React, { useState } from 'react';
import { OnboardingData, OSPWeakness, ZSVWeakness, BothPriority } from '../../types/onboarding';

interface Props {
    onNext: (data: Partial<OnboardingData>) => void;
    onBack: () => void;
    initialData: Partial<OnboardingData>;
    isTransitioning?: boolean;
    subStep?: 'osp' | 'zsv'; // Received from parent
}

const OnboardingFocus: React.FC<Props> = ({ onNext, initialData, isTransitioning, subStep = 'osp' }) => {
    const track = initialData.track;
    const [ospWeakness, setOspWeakness] = useState<OSPWeakness | undefined>(undefined);
    const [zsvWeakness, setZsvWeakness] = useState<ZSVWeakness | undefined>(undefined);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [bothPriority] = useState<BothPriority>('balanced'); // Logic handled automatically now

    const handleSelectOsp = (weakness: OSPWeakness) => {
        setOspWeakness(weakness);
        // Parent determines if we go to ZSV (Both track) or Finish (OSP track)
        setTimeout(() => {
            onNext({ ospLevel: 'ok', ospWeakness: weakness, zsvLevel: 'ok', zsvWeakness: 'unsure', bothPriority: 'balanced' });
        }, 350);
    };

    const handleSelectZsv = (weakness: ZSVWeakness) => {
        setZsvWeakness(weakness);
        setTimeout(() => {
            onNext({
                ospLevel: 'ok',
                ospWeakness: track === 'ZSV' ? 'unsure' : (ospWeakness || 'unsure'), // Use stored ospWeakness or default safe
                zsvLevel: 'ok',
                zsvWeakness: weakness,
                bothPriority: 'balanced'
            });
        }, 350);
    };

    const Option: React.FC<{
        label: string,
        isSelected: boolean,
        onClick: () => void
    }> = ({ label, isSelected, onClick }) => (
        <button
            onClick={onClick}
            disabled={isTransitioning}
            className={`w-full px-6 py-3.5 rounded-xl border-2 border-b-[6px] transition-all duration-200 text-base font-bold ${isSelected
                ? 'border-brand-orange bg-[#fed7aa] text-brand-orange shadow-sm translate-y-[2px]'
                : 'border-gray-300 dark:border-gray-600 hover:border-brand-orange/50 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
                } active:border-b-2 active:translate-y-[6px] disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            {label}
        </button>
    );

    // Determine what to show based on track and subStep
    // Note: Parent handles the subStep switching for BOTH track.
    const showOsp = track === 'OSP' || (track === 'BOTH' && subStep === 'osp');
    const showZsv = track === 'ZSV' || (track === 'BOTH' && subStep === 'zsv');

    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-right-4 duration-300">
            {/* OSP Weakness */}
            {showOsp && (
                <div>
                    <label className="block text-gray-900 dark:text-white font-bold text-2xl mb-2">
                        {track === 'BOTH' ? 'OSP: Co ti dělá největší problém?' : 'Co ti dělá největší problém v OSP?'}
                    </label>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
                        Vyber oblast, které chceš věnovat nejvíce pozornosti
                    </p>
                    <div className="flex flex-col gap-3 max-w-md mx-auto">
                        <Option label="Logika" isSelected={ospWeakness === 'logic'} onClick={() => handleSelectOsp('logic')} />
                        <Option label="Práce s textem" isSelected={ospWeakness === 'reading'} onClick={() => handleSelectOsp('reading')} />
                        <Option label="Tabulky / data" isSelected={ospWeakness === 'data'} onClick={() => handleSelectOsp('data')} />
                        <Option label="Nevím" isSelected={ospWeakness === 'unsure'} onClick={() => handleSelectOsp('unsure')} />
                    </div>
                </div>
            )}

            {/* ZSV Weakness */}
            {showZsv && (
                <div>
                    <label className="block text-gray-900 dark:text-white font-bold text-2xl mb-2">
                        {track === 'BOTH' ? 'ZSV: Kde máš největší mezery?' : 'Kde máš největší mezery v ZSV?'}
                    </label>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
                        Vyber oblast, které chceš věnovat nejvíce pozornosti
                    </p>
                    <div className="flex flex-col gap-3 max-w-md mx-auto">
                        <Option label="Filosofie" isSelected={zsvWeakness === 'filosofie'} onClick={() => handleSelectZsv('filosofie')} />
                        <Option label="Sociologie" isSelected={zsvWeakness === 'sociologie'} onClick={() => handleSelectZsv('sociologie')} />
                        <Option label="Politologie" isSelected={zsvWeakness === 'politologie'} onClick={() => handleSelectZsv('politologie')} />
                        <Option label="Nevím" isSelected={zsvWeakness === 'unsure'} onClick={() => handleSelectZsv('unsure')} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingFocus;
